/**
 * DriveRow.jsx — Single drive row inside a DriveDayCard.
 *
 * Mobile: stacked layout with 44px touch targets.
 * Desktop: horizontal single-line layout.
 */

import { useState } from 'react';
import {
  Box, Typography, Chip, Stack, Select, MenuItem, FormControl,
  IconButton, Tooltip, Menu,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonalIcon,
  HelpOutline as UntaggedIcon,
  CheckCircle as ConfirmedIcon,
  AutoAwesome as SuggestIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { useMobile } from '../../hooks/useMobile';

const TAG_COLORS = { business: 'success', personal: 'error', untagged: 'default' };
const TAG_ICONS = {
  business: <BusinessIcon fontSize="small" />,
  personal: <PersonalIcon fontSize="small" />,
  untagged: <UntaggedIcon fontSize="small" />,
};

const REASON_LABELS = {
  tessie_profile: 'Tesla profile',
  sticky: 'prev drive',
  schedule: 'on schedule',
};

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function fmtMiles(m) {
  if (m == null) return '—';
  return `${parseFloat(m).toFixed(1)} mi`;
}
function shortLoc(loc) {
  if (!loc) return '—';
  return loc.replace(/, United States$/, '').replace(/, California \d{5}/, ', CA');
}

function buildMapUrl(drive) {
  const hasStart = drive.startLat != null && drive.startLng != null;
  const hasEnd = drive.endLat != null && drive.endLng != null;
  const origin = hasStart ? `${drive.startLat},${drive.startLng}` : (drive.startLocation || '');
  const destination = hasEnd ? `${drive.endLat},${drive.endLng}` : (drive.endLocation || '');
  if (!origin && !destination) return null;
  if (!destination || !origin) {
    const q = origin || destination;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  }
  const params = new URLSearchParams({ api: '1', origin, destination, travelmode: 'driving' });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

export default function DriveRow({ drive, roster, onClassify, onAssignDriver, onAcceptSuggestion, onOpenMap }) {
  const { isMobile } = useMobile();
  const [tagMenuAnchor, setTagMenuAnchor] = useState(null);

  const suggested = drive.suggestedRosterId
    ? roster.find((r) => r.id === drive.suggestedRosterId)
    : null;

  const handleTagClick = (e) => {
    // For untagged drives open a chooser; else toggle directly
    if (drive.tag === 'untagged') {
      setTagMenuAnchor(e.currentTarget);
    } else {
      onClassify(drive.id, drive.tag === 'business' ? 'personal' : 'business');
    }
  };

  const DriverSelect = (
    <FormControl size="small" variant="standard" sx={{ minWidth: 110 }}>
      <Select
        value={drive.rosterId || ''}
        onChange={(e) => onAssignDriver(drive.id, e.target.value)}
        displayEmpty
        disableUnderline={!!drive.rosterId}
        sx={{
          fontSize: '0.875rem',
          '& .MuiSelect-select': { py: 0.5, minHeight: 'unset' },
          color: drive.rosterId ? 'text.primary' : 'text.secondary',
        }}
      >
        <MenuItem value="" disabled><em>Assign driver</em></MenuItem>
        {roster.map((r) => (
          <MenuItem key={r.id} value={r.id}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {r.accentColor && (
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.accentColor, flexShrink: 0 }} />
              )}
              <span>{r.name}</span>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const TagChip = (
    <Chip
      icon={TAG_ICONS[drive.tag]}
      label={drive.tag}
      color={TAG_COLORS[drive.tag]}
      size="small"
      onClick={handleTagClick}
      sx={{ cursor: 'pointer', textTransform: 'capitalize', minHeight: isMobile ? 40 : 32 }}
    />
  );

  const SuggestChip = suggested && !drive.rosterId ? (
    <Tooltip title={`Accept suggestion (${REASON_LABELS[drive.suggestionReason] || drive.suggestionReason})`}>
      <Chip
        icon={<SuggestIcon sx={{ fontSize: '14px !important' }} />}
        label={`${suggested.name}?`}
        size="small"
        variant="outlined"
        color="info"
        onClick={() => onAcceptSuggestion(drive.id, drive.suggestedRosterId)}
        sx={{ cursor: 'pointer', minHeight: isMobile ? 36 : 28 }}
      />
    </Tooltip>
  ) : null;

  const mapUrl = buildMapUrl(drive);
  const hasAnyMap = !!mapUrl;
  const MapButton = hasAnyMap ? (
    <Tooltip title="View on map">
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          if (onOpenMap) {
            onOpenMap(drive);
          } else {
            window.open(mapUrl, '_blank', 'noopener,noreferrer');
          }
        }}
        sx={{
          width: 36,
          height: 36,
          color: 'primary.main',
          border: '1px solid',
          borderColor: 'primary.light',
          borderRadius: 1,
          '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
        }}
        aria-label="View drive on map"
      >
        <MapIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  if (isMobile) {
    return (
      <Box
        sx={{
          p: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          opacity: drive.confirmed ? 0.85 : 1,
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        {/* Row 1: time + miles + confirmed icon */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {fmtTime(drive.startedAt)} – {fmtTime(drive.endedAt)}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {fmtMiles(drive.distanceMiles)}
            </Typography>
            {drive.confirmed && (
              <ConfirmedIcon fontSize="small" color="success" />
            )}
          </Stack>
        </Stack>

        {/* Row 2: locations */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              minWidth: 0,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {shortLoc(drive.startLocation)} → {shortLoc(drive.endLocation)}
          </Typography>
          {MapButton}
        </Stack>

        {/* Row 3: driver + tag */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
          {DriverSelect}
          {SuggestChip}
          <Box sx={{ flex: 1 }} />
          {TagChip}
        </Stack>

        {/* Tag picker menu for untagged */}
        <Menu anchorEl={tagMenuAnchor} open={!!tagMenuAnchor} onClose={() => setTagMenuAnchor(null)}>
          <MenuItem onClick={() => { setTagMenuAnchor(null); onClassify(drive.id, 'business'); }}>
            <BusinessIcon fontSize="small" color="success" sx={{ mr: 1 }} /> Business
          </MenuItem>
          <MenuItem onClick={() => { setTagMenuAnchor(null); onClassify(drive.id, 'personal'); }}>
            <PersonalIcon fontSize="small" color="error" sx={{ mr: 1 }} /> Personal
          </MenuItem>
        </Menu>
      </Box>
    );
  }

  // Desktop horizontal layout
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        opacity: drive.confirmed ? 0.9 : 1,
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ minWidth: 110 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {fmtTime(drive.startedAt)} – {fmtTime(drive.endedAt)}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 140 }}>
        {DriverSelect}
      </Box>
      <Box sx={{ minWidth: 70, textAlign: 'right' }}>
        <Typography variant="body2" fontWeight={500}>
          {fmtMiles(drive.distanceMiles)}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Tooltip title={`${drive.startLocation || ''} → ${drive.endLocation || ''}`}>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {shortLoc(drive.startLocation)} → {shortLoc(drive.endLocation)}
          </Typography>
        </Tooltip>
      </Box>
      {SuggestChip}
      {TagChip}
      {MapButton}
      <Box sx={{ width: 24, textAlign: 'center' }}>
        {drive.confirmed ? (
          <Tooltip title={`Confirmed by ${drive.confirmedBy || '?'}`}>
            <ConfirmedIcon fontSize="small" color="success" />
          </Tooltip>
        ) : null}
      </Box>
      <Menu anchorEl={tagMenuAnchor} open={!!tagMenuAnchor} onClose={() => setTagMenuAnchor(null)}>
        <MenuItem onClick={() => { setTagMenuAnchor(null); onClassify(drive.id, 'business'); }}>
          <BusinessIcon fontSize="small" color="success" sx={{ mr: 1 }} /> Business
        </MenuItem>
        <MenuItem onClick={() => { setTagMenuAnchor(null); onClassify(drive.id, 'personal'); }}>
          <PersonalIcon fontSize="small" color="error" sx={{ mr: 1 }} /> Personal
        </MenuItem>
      </Menu>
    </Box>
  );
}
